import {useState, useEffect, useRef} from 'react'
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  FormHelperText
} from '@material-ui/core'
import {
  surveySmsTemplateKeys,
  surveySmsUrlPlaceholder,
  validateSurveySmsTemplate,
  buildSurveySmsPreview
} from '../../utils/surveySmsTemplate'

export default function MakeSurveySms (props) {
  const [smsBody, setSmsBody] = useState('')
  const [excludeSuffix, setExcludeSuffix] = useState(false)
  const [errors, setErrors] = useState([])
  const inputRef = useRef(null)
  const cursorRef = useRef(null)

  const onClose = () => {
    props.onClose()
  }

  const validate = (body, exclude) => {
    return validateSurveySmsTemplate({body, excludeSuffix: exclude})
  }

  const onAdd = () => {
    const result = validate(smsBody, excludeSuffix)

    if (!result.valid) {
      setErrors(result.errors)
      return
    }

    props.onClose({success: true, smsBody, excludeSuffix})
  }

  useEffect(() => {
    const body = props.smsBody || ''
    const exclude = Boolean(props.excludeSuffix)

    if (props.smsBody) {
      setSmsBody(props.smsBody)
    }

    setExcludeSuffix(exclude)
    setErrors(validate(body, exclude).errors)
  }, [])

  const onBodyChange = (value) => {
    setSmsBody(value)
    setErrors(validate(value, excludeSuffix).errors)
  }

  const onExcludeChange = (checked) => {
    setExcludeSuffix(checked)
    setErrors(validate(smsBody, checked).errors)
  }

  const rememberCursor = (event) => {
    const el = event.target
    cursorRef.current = {
      start: el.selectionStart,
      end: el.selectionEnd
    }
  }

  const insertKey = (key) => {
    const token = `{{${key}}}`
    const start = cursorRef.current ? cursorRef.current.start : smsBody.length
    const end = cursorRef.current ? cursorRef.current.end : smsBody.length
    const next = `${smsBody.slice(0, start)}${token}${smsBody.slice(end)}`

    if (next.length > 1000) {
      return
    }

    const nextCursor = start + token.length
    cursorRef.current = {start: nextCursor, end: nextCursor}
    onBodyChange(next)

    requestAnimationFrame(() => {
      const el = inputRef.current
      if (!el) {
        return
      }
      el.focus()
      el.setSelectionRange(nextCursor, nextCursor)
    })
  }

  const preview = buildSurveySmsPreview({
    smsBody,
    excludeSuffix,
    suffix: props.suffix,
    urlPlaceholder: props.urlPlaceholder || surveySmsUrlPlaceholder
  })

  return (
    <Dialog open={props.open} maxWidth={'sm'} fullWidth aria-labelledby="Custom SMS message">
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent style={{minHeight: 300}}>
        <DialogContentText>
          {props.description}
        </DialogContentText>

        <TextField
          autoFocus
          margin="dense"
          name="smsBody"
          label="SMS Body"
          type="textarea"
          value={smsBody}
          onChange={(e) => onBodyChange(e.target.value)}
          fullWidth
          multiline
          minRows={6}
          maxRows={10}
          inputProps={{maxLength: 1000}}
          inputRef={inputRef}
          onSelect={rememberCursor}
          onBlur={rememberCursor}
          onClick={rememberCursor}
          onKeyUp={rememberCursor}
          helperText={`${smsBody.length}/1000`}
          error={errors.length > 0}
        />

        <Typography variant="subtitle2" style={{color: 'rgba(0, 0, 0, 0.54)', marginTop: 8}}>
          Insert template keys (can be used more than once):
        </Typography>
        <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4, marginBottom: 8}}>
          {surveySmsTemplateKeys.map(key => (
            <Button key={key} size="small" variant="outlined" onClick={() => insertKey(key)}>
              {`{{${key}}}`}
            </Button>
          ))}
        </div>

        <FormControlLabel
          control={
            <Checkbox
              checked={excludeSuffix}
              onChange={(e) => onExcludeChange(e.target.checked)}
              name="excludeSuffix"
            />
          }
          label="Exclude default suffix"
        />

        <Typography variant="subtitle2" style={{color: 'rgba(0, 0, 0, 0.54)'}}>
          {excludeSuffix
            ? 'When excluding the default suffix, include {{event_name}} and {{survey_url}} in your message. The URL is only appended automatically if {{survey_url}} is missing.'
            : 'The default suffix already includes the event name and survey URL. Template keys in the body are optional and will be filled in when the message is sent.'}
        </Typography>

        {errors.length ? (
          <FormHelperText error>
            {errors.join(' ')}
          </FormHelperText>
        ) : null}

        <br />
        <Typography color="textSecondary" gutterBottom>
          Preview
        </Typography>
        <Card style={{boxShadow: 'none', backgroundColor: '#ececec'}}>
          <CardContent>
            <Typography variant="body2" component="p">
              {preview}
            </Typography>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">Close</Button>
        <Button onClick={onAdd} color="primary" disabled={errors.length > 0}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}
